import { makeAutoObservable, runInAction } from "mobx";
import RootStore from "../../../stores/RootStore";
import ServiceStore from "../../../stores/ServiceStore";

class JobFieldReportingDashboardStore {
  // Observables
  isLoading = false;
  error: string | null = null;
  jobNumber: string | null = null;
  panelStats = {
    totalPanels: 0,
    installedPanels: 0,
    inProgressPanels: 0,
  };
  hardwareStats = {
    totalOrders: 0,
    pendingOrders: 0,
    completedOrders: 0,
  };
  recentActivities: any[] = [];
  issuesStats = {
    totalIssues: 0,
    openIssues: 0,
    resolvedIssues: 0,
  };

  constructor(private rootStore: RootStore) {
    makeAutoObservable(this);
  }

  // Actions
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  setJobNumber(jobNumber: string | null) {
    this.jobNumber = jobNumber;
  }

  // Async actions
  async loadJobDashboardData(jobNumber: string) {
    this.setLoading(true);
    this.setError(null);
    this.setJobNumber(jobNumber);

    try {
      // Load panels data for specific job
      await (this.rootStore.panelStore as ServiceStore).find({
        query: {
          jobNumber,
          $limit: 1000,
        },
      });

      // Load field activities for specific job
      await (this.rootStore.fieldActivityStore as ServiceStore).find({
        query: {
          jobNumber,
          $limit: 20,
          $sort: { activityDate: -1, activityTime: -1 },
        },
      });

      // Load hardware orders for specific job
      await (this.rootStore.hardwareOrdersStore as ServiceStore).find({
        query: {
          jobNumber,
          $limit: 1000,
        },
      });

      // Load field activity issues for specific job
      await (this.rootStore.fieldActivityIssuesStore as ServiceStore).find({
        query: {
          $limit: 1000,
        },
      });

      runInAction(() => {
        // Calculate panel statistics for this job
        const panels = (
          this.rootStore.panelStore as ServiceStore
        ).entities.filter((p: any) => p.jobNumber === jobNumber);

        this.panelStats.totalPanels = panels.length;
        this.panelStats.installedPanels = panels.filter(
          (p: any) => p.installationDate && p.installationDate !== "1970-01-01"
        ).length;
        this.panelStats.inProgressPanels = panels.filter(
          (p: any) =>
            p.castingDate &&
            p.castingDate !== "1970-01-01" &&
            (!p.installationDate || p.installationDate === "1970-01-01")
        ).length;

        // Calculate hardware statistics for this job
        const hardwareOrders = (
          this.rootStore.hardwareOrdersStore as ServiceStore
        ).entities.filter((h: any) => h.jobNumber === jobNumber);

        this.hardwareStats.totalOrders = hardwareOrders.length;
        this.hardwareStats.pendingOrders = hardwareOrders.filter(
          (h: any) => h.status === "pending" || !h.status
        ).length;
        this.hardwareStats.completedOrders = hardwareOrders.filter(
          (h: any) => h.status === "completed"
        ).length;

        // Set recent activities for this job
        this.recentActivities = (
          this.rootStore.fieldActivityStore as ServiceStore
        ).entities
          .filter((a: any) => a.jobNumber === jobNumber)
          .slice(0, 20);

        // Calculate issues statistics for this job
        const issues = (
          this.rootStore.fieldActivityIssuesStore as ServiceStore
        ).entities.filter((i: any) => i.jobNumber === jobNumber);

        this.issuesStats.totalIssues = issues.length;
        this.issuesStats.openIssues = issues.filter(
          (i: any) =>
            i.status === "open" || i.status === "in_progress" || !i.status
        ).length;
        this.issuesStats.resolvedIssues = issues.filter(
          (i: any) => i.status === "resolved" || i.status === "closed"
        ).length;
      });
    } catch (error) {
      console.error("Error loading job dashboard data:", error);
      runInAction(() => {
        this.setError("Failed to load job dashboard data");
      });
    } finally {
      runInAction(() => {
        this.setLoading(false);
      });
    }
  }

  async refreshData() {
    if (this.jobNumber) {
      await this.loadJobDashboardData(this.jobNumber);
    }
  }

  // Computed values
  get isDataLoaded() {
    return !this.isLoading && !this.error;
  }

  get jobDetails() {
    if (!this.jobNumber) return null;
    return (this.rootStore.jobStore as ServiceStore).entities.find(
      (j: any) => j.jobNumber === this.jobNumber
    );
  }

  get installationProgress() {
    if (this.panelStats.totalPanels === 0) return 0;
    return Math.round(
      (this.panelStats.installedPanels / this.panelStats.totalPanels) * 100
    );
  }

  get hardwareCompletionRate() {
    if (this.hardwareStats.totalOrders === 0) return 0;
    return Math.round(
      (this.hardwareStats.completedOrders / this.hardwareStats.totalOrders) *
        100
    );
  }
}

export default JobFieldReportingDashboardStore;
